import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { Button, Icon, Panes, Select, VisuallyHidden } from '@wtfalch/design';

import { useEffect, useRef, useState } from 'react';

import { useListNav } from '../shortcuts.tsx';
import { Avatar } from '../ui/Avatar.tsx';
import {
  groupRooms,
  type ListView,
  lastMessageLabel,
  mentionCount,
  unreadCountLabel,
  viewRooms,
} from './format.ts';
import { AvatarWithPresence, directPeerId, StatusLabel } from './presence.tsx';
import { BrowseRooms, NewRoom } from './RoomDialogs.tsx';

/**
 * The room list: channels, then direct messages, each a `RoomRow` --
 * matching the canvas's `chat_list()`. An unanswered invitation gets its own
 * group above both, the same shape a channel or a DM has, so accepting or
 * declining it is a press on the row itself rather than a screen of its own.
 *
 * A row also draws one line under the name for `ChatRoomSummary.
 * lastMessage` (`@wtfalch/chat` 0.5.0) -- see `lastMessageLabel` in
 * `format.ts` for the exact shape, "You: ..."/"<sender>: ..."/the bare
 * body, and nothing at all once `lastMessage` is `null`.
 *
 * `filter`/`guest` glyphs the canvas's mock also carries are still not
 * drawn -- the SDK has nothing behind them, so the row draws only what it
 * can back: a kind, a name, the last message, whether it is muted (#253)
 * and, for a direct room, its other side's presence (#254).
 *
 * Unread draws three ways: weight (bold) on any unread room, its count in
 * a quiet number, and an accent badge only on one where somebody tagged you
 * (`mentionCount`, `format.ts`) -- so a forty-message thread nobody needs
 * stops shouting as loudly as a direct question (#295; the quiet count is
 * from the October design review).
 */
export function ChatList({
  client,
  rooms,
  loading,
  error,
  onRetry,
  selectedRoomId,
  view,
  onViewChange,
  onSelectRoom,
  onAnswerInvitation,
  encryptedIds,
  onOpenEncryption,
  initialDialog = null,
}: {
  client: ChatClient;
  rooms: readonly ChatRoomSummary[];
  loading: boolean;
  /** Why the room list did not load, when it did not (`useChatRooms`). */
  error?: Error | null;
  onRetry?: () => void;
  selectedRoomId: string | null;
  view: ListView;
  onViewChange: (view: ListView) => void;
  onSelectRoom: (roomId: string) => void;
  onAnswerInvitation: (roomId: string, accept: boolean) => void;
  /** Rooms that are end-to-end encrypted (agora#135). */
  encryptedIds?: ReadonlySet<string>;
  /** Opens the recovery key and device verification dialog. */
  onOpenEncryption?: () => void;
  /** A dialog to open on mount; the preview states use it. */
  initialDialog?: 'new' | 'browse' | null;
}) {
  const [dialog, setDialog] = useState<'new' | 'browse' | null>(null);
  // `browseRooms` answers empty until the session has started.
  const opened = useRef(false);
  useEffect(() => {
    if (loading || !initialDialog || opened.current) return;
    opened.current = true;
    setDialog(initialDialog);
  }, [loading, initialDialog]);
  const { invitations, channels, directs } = groupRooms(rooms);
  const sortedChannels = viewRooms(channels, view);
  const sortedDirects = viewRooms(directs, view);

  // The shared list navigation (agora#221, generalising agora#27): `j`/`k`
  // open the next/previous room. Invitations sit above both groups but are
  // not part of this navigation -- answering one is its own row action.
  const ids = [...sortedChannels, ...sortedDirects].map((r) => r.id);
  useListNav(ids, selectedRoomId, onSelectRoom);

  return (
    <Panes.Column width={280} surface="panel-2" className="chat-list">
      <div className="flex flex-col chat-list-head">
        <div className="flex items-center gap-2">
          <div className="grow min-w-0">
            <Select
              block
              size="sm"
              aria-label="Show rooms"
              value={view}
              onChange={(e) => onViewChange(e.target.value as ListView)}
            >
              <option value="all">All</option>
              <option value="unread">Unread first</option>
              <option value="mentions">Mentions</option>
            </Select>
          </div>
          <Button kind="ghost" size="sm" onPress={() => setDialog('new')}>
            <Icon name="plus" size={14} />
            New room
          </Button>
          <Button kind="ghost" size="sm" disabled={loading} onPress={() => setDialog('browse')}>
            Browse
          </Button>
          {onOpenEncryption && (
            <Button
              kind="ghost"
              size="sm"
              iconOnly
              aria-label="Encryption"
              onPress={onOpenEncryption}
            >
              <Icon name="lock" size={16} />
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col chat-list-rows">
        {loading && rooms.length === 0 && (
          <p className="text-xs text-muted chat-list-status">Loading…</p>
        )}
        {!loading && rooms.length === 0 && error && (
          <div className="flex flex-col items-start gap-2 chat-list-status">
            <p className="text-xs text-bad m-0" role="alert">
              Chat could not load. {error.message}
            </p>
            {onRetry && (
              <Button size="sm" onPress={onRetry}>
                Try again
              </Button>
            )}
          </div>
        )}
        {!loading && rooms.length === 0 && !error && (
          <p className="text-xs text-muted chat-list-status">You are not in any rooms yet.</p>
        )}
        {invitations.length > 0 && (
          <>
            <div className="navgrp">Invitations</div>
            {invitations.map((room) => (
              <InvitationRow
                key={room.id}
                client={client}
                room={room}
                onAnswer={(accept) => onAnswerInvitation(room.id, accept)}
              />
            ))}
          </>
        )}
        {channels.length > 0 && (
          <>
            <div className="navgrp">Rooms</div>
            {sortedChannels.map((room) => (
              <RoomRow
                key={room.id}
                client={client}
                room={room}
                selected={room.id === selectedRoomId}
                encrypted={encryptedIds?.has(room.id) ?? false}
                onClick={() => onSelectRoom(room.id)}
              />
            ))}
          </>
        )}
        {directs.length > 0 && (
          <>
            <div className="navgrp">People</div>
            {sortedDirects.map((room) => (
              <RoomRow
                key={room.id}
                client={client}
                room={room}
                selected={room.id === selectedRoomId}
                encrypted={encryptedIds?.has(room.id) ?? false}
                onClick={() => onSelectRoom(room.id)}
              />
            ))}
          </>
        )}
      </div>
      {dialog === 'new' && (
        <NewRoom
          client={client}
          onClose={() => setDialog(null)}
          onCreated={(roomId) => {
            setDialog(null);
            onSelectRoom(roomId);
          }}
        />
      )}
      {dialog === 'browse' && <BrowseRooms client={client} onClose={() => setDialog(null)} />}
    </Panes.Column>
  );
}

function RoomRow({
  client,
  room,
  selected,
  encrypted,
  onClick,
}: {
  client: ChatClient;
  room: ChatRoomSummary;
  selected: boolean;
  encrypted: boolean;
  onClick: () => void;
}) {
  const unread = room.unreadCount > 0;
  const mentions = mentionCount(room);
  const peerId = directPeerId(client, room);
  const preview = lastMessageLabel(room);
  const classes = ['roomrow', selected && 'on', unread && 'new', room.muted && 'muted']
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classes} onClick={onClick}>
      {room.isDirect ? (
        peerId ? (
          <AvatarWithPresence
            client={client}
            userId={peerId}
            name={room.name}
            seed={room.id}
            size="sm"
          />
        ) : (
          <Avatar name={room.name} seed={room.id} size="sm" />
        )
      ) : (
        <span className="flex flex-none items-center justify-center w-5.5 text-muted roomrow-icon">
          <Icon name="hash" size={16} />
        </span>
      )}
      <span className="flex flex-col grow min-w-0 gap-0.5 chat-row-lines">
        <span className="flex items-center gap-3">
          <span
            className={
              unread ? 'text-sm font-strong truncate grow min-w-0' : 'text-sm truncate grow min-w-0'
            }
          >
            {room.name}
          </span>
          {room.isDirect && <StatusLabel client={client} userId={peerId} />}
          {encrypted && (
            <span className="flex flex-none items-center text-muted">
              <Icon name="lock" size={14} />
              <VisuallyHidden>Encrypted</VisuallyHidden>
            </span>
          )}
          {room.muted && (
            <span className="flex flex-none items-center text-muted roomrow-muted-icon">
              <Icon name="bell-off" size={14} />
              <VisuallyHidden>Muted</VisuallyHidden>
            </span>
          )}
          {/* How many are unread, in a quiet number: the room's own
              `unreadCount`, capped, so a busy room does not widen its row. */}
          {unread && (
            <span className="text-xs chat-unread-count">
              {unreadCountLabel(room.unreadCount)}
              <VisuallyHidden> unread</VisuallyHidden>
            </span>
          )}
          {/* A mention badge still shows on a muted row (#253): muting
              stops the bell, never the count. */}
          {mentions > 0 && (
            <span className="unread text-sm">
              {mentions}
              <VisuallyHidden>{mentions === 1 ? ' mention' : ' mentions'}</VisuallyHidden>
            </span>
          )}
        </span>
        {/* The last message (`@wtfalch/chat` 0.5.0), one truncating line --
            `null` (nothing sent yet, or an unanswered invitation, which
            never reaches this row) draws nothing rather than an empty
            second line. */}
        {preview !== null && <span className="text-xs text-muted truncate">{preview}</span>}
      </span>
    </button>
  );
}

function InvitationRow({
  client,
  room,
  onAnswer,
}: {
  client: ChatClient;
  room: ChatRoomSummary;
  onAnswer: (accept: boolean) => void;
}) {
  const peerId = directPeerId(client, room);
  return (
    <div className="flex items-center gap-3" style={{ padding: '7px 16px' }}>
      {room.isDirect ? (
        peerId ? (
          <AvatarWithPresence
            client={client}
            userId={peerId}
            name={room.name}
            seed={room.id}
            size="sm"
          />
        ) : (
          <Avatar name={room.name} seed={room.id} size="sm" />
        )
      ) : (
        <span className="flex flex-none items-center justify-center w-5.5 text-muted roomrow-icon">
          <Icon name="hash" size={16} />
        </span>
      )}
      <span className="text-sm truncate grow min-w-0">{room.name}</span>
      <Button
        kind="ghost"
        size="sm"
        iconOnly
        aria-label={`Accept ${room.name}`}
        onPress={() => onAnswer(true)}
      >
        <Icon name="check" size={16} />
      </Button>
      <Button
        kind="ghost"
        size="sm"
        iconOnly
        aria-label={`Decline ${room.name}`}
        onPress={() => onAnswer(false)}
      >
        <Icon name="close" size={16} />
      </Button>
    </div>
  );
}
